import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";
import ScrollReveal from "./ScrollReveal";

interface PremiumCardProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  iconEmoji?: string;
  children?: ReactNode;
  className?: string;
  delay?: number;
  variant?: "default" | "glass" | "gradient" | "outline" | "elevated";
  number?: number;
  align?: "left" | "center";
  href?: string;
  onClick?: () => void;
}

const PremiumCard = ({
  title,
  description,
  icon: Icon,
  iconEmoji,
  children,
  className,
  delay = 0,
  variant = "default",
  number,
  align = "center",
  href,
  onClick,
}: PremiumCardProps) => {
  const variantClasses = {
    default: "bg-card border border-border/50 shadow-sm hover:shadow-premium",
    glass: "glass-card hover:shadow-premium",
    gradient: "bg-gradient-to-br from-card to-muted border border-border/30 shadow-sm hover:shadow-premium",
    outline: "bg-transparent border-2 border-border hover:border-secondary/50 hover:bg-card/50",
    elevated: "bg-card border border-border/50 shadow-premium-lg hover:shadow-premium-lg hover:-translate-y-1",
  };

  const Wrapper = href ? "a" : onClick ? "button" : "div";
  const wrapperProps = href ? { href } : onClick ? { onClick } : {};

  return (
    <ScrollReveal animation="fade-up" delay={delay}>
      <Wrapper
        {...(wrapperProps as any)}
        className={cn(
          "relative group rounded-xl overflow-hidden transition-all duration-500",
          variantClasses[variant],
          (href || onClick) && "cursor-pointer",
          className
        )}
      >
        {/* Number Badge */}
        {number && (
          <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center">
            <span className="text-sm font-bold text-secondary">{number}</span>
          </div>
        )}

        {/* Gradient Border Effect on Hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
          <div className="absolute inset-0 rounded-xl border border-secondary/20" />
        </div>

        <div className={cn("relative p-6", align === "center" && "text-center")}>
          {/* Icon */}
          {(Icon || iconEmoji) && (
            <div
              className={cn(
                "h-14 w-14 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110",
                "bg-gradient-to-br from-secondary/10 to-secondary/5 border border-secondary/10",
                align === "center" && "mx-auto"
              )}
            >
              {Icon ? (
                <Icon className="h-7 w-7 text-secondary" />
              ) : (
                <span className="text-2xl">{iconEmoji}</span>
              )}
            </div>
          )}

          {/* Title */}
          <h3 className="font-bold text-lg mb-2 group-hover:text-secondary transition-colors">
            {title}
          </h3>

          {/* Description */}
          {description && (
            <p className="text-muted-foreground text-sm leading-relaxed">
              {description}
            </p>
          )}

          {/* Additional Content */}
          {children}
        </div>
      </Wrapper>
    </ScrollReveal>
  );
};

export default PremiumCard;
